import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import { useProfileAgreement } from '@/hooks/queries/use-profile-agreement';
import { useProfileFcaQuestionnaireEligibility } from '@/hooks/queries/use-profile-fca-questionnaire';
import { useProfileIdentity } from '@/hooks/queries/use-profile-identity';
import { useProfileVerificationInformation } from '@/hooks/queries/use-profile-verification-information';
import { useProfileVerificationInformationSupportingInformation } from '@/hooks/queries/use-profile-verification-information-supporting-information';
import LoadingPage from '@/pages/loading';
import { WORKFLOW_LEVEL_IDENTIFIER } from '@/services/api/profile';
import { AgreementKey } from '@/services/api/profile/agreement';

const OnboardingInitialRedirect = () => {
  const { isLoading: isLoadingServicesAgreement, data: servicesAgreement } = useProfileAgreement(AgreementKey.SERVICES_AGREEMENT);
  const { isLoading: isLoadingIdentity, isComplete: isCompleteIdentity, data: identity } = useProfileIdentity();
  const { isLoading: isLoadingFcaQuestionnaire, isComplete: isCompleteFcaQuestionnaire } = useProfileFcaQuestionnaireEligibility(identity?.addressOfResidency.country);
  const { isLoading: isLoadingVerificationInformation, data: verificationInformation } = useProfileVerificationInformation();
  const isWorkflowVerificationLevel = !!verificationInformation?.verification?.level?.includes(WORKFLOW_LEVEL_IDENTIFIER);
  const { isLoading: isLoadingVerificationInformationSupportingInformation, isComplete: isCompleteVerificationInformationSupportingInformationQuery } =
    useProfileVerificationInformationSupportingInformation(isWorkflowVerificationLevel);
  const { t } = useTranslation();
  const isCompleteVerificationInformationSupportingInformation = isWorkflowVerificationLevel ? !!isCompleteVerificationInformationSupportingInformationQuery : true;
  const isLoading =
    isLoadingServicesAgreement || isLoadingIdentity || isLoadingFcaQuestionnaire || isLoadingVerificationInformation || isLoadingVerificationInformationSupportingInformation;

  if (isLoading)
    return (
      <LoadingPage>
        <p>{t('redirectingToNextStep')}</p>
      </LoadingPage>
    );

  if (!servicesAgreement) return <Navigate to="/onboarding/services-agreement" replace />;

  if (!isCompleteIdentity) return <Navigate to="/onboarding/personal-information" replace />;

  if (!isCompleteFcaQuestionnaire) return <Navigate to="/onboarding/fca-questionnaire" replace />;

  if (!verificationInformation || verificationInformation.verification.status === 'NOT_VERIFIED') return <Navigate to="/onboarding/verification" replace />;

  if (!isCompleteVerificationInformationSupportingInformation) return <Navigate to="/onboarding/verification/supporting-information" replace />;

  if (!verificationInformation.aml.date) return <Navigate to="/onboarding/aml-questionnaire" replace />;

  return <Navigate to="/onboarding/complete" replace />;
};
OnboardingInitialRedirect.displayName = 'OnboardingInitialRedirect';

export default OnboardingInitialRedirect;
